/**
 * Renders answer text with `[n]` markers turned into clickable footnotes.
 *
 * The markers are the mechanism behind golden rule 3 as an employee experiences it: a number in
 * the sentence that opens the document it came from. So they are real buttons — keyboard
 * reachable, labelled, and focusable — rather than styled spans.
 *
 * A marker that does not correspond to a provided source is rendered as plain text rather than as
 * a dead button. The backend already drops and records invented markers; this is the display-side
 * consequence, and a footnote that goes nowhere is worse than no footnote because it looks
 * verified.
 */

import { useTranslation } from "react-i18next";

import { CitationChip, type Citation } from "@nab/shared";

export interface CitationMarkersProps {
  text: string;
  citations: Citation[];
  onCitationClick: (index: number) => void;
}

/**
 * `[1]`, `[12]`, and the Arabic-Indic form a model writing Arabic produces.
 *
 * The optional label matches the server's parser in `app/chat/citations.py` — observed on the
 * deployed model, answering in Arabic: `[source 1]` on every sentence. The two must agree exactly,
 * or a marker the server resolved renders here as plain text and the footnote it earned is
 * invisible.
 *
 * Digits are still required, so `[A]` and `[see below]` stay prose.
 */
const MARKER = /\[\s*(?:(?:source|src|ref|reference|المصدر|مصدر)\s*[:#.\-]?\s*)?([0-9٠-٩]{1,3})\s*\]/gi;

/** Arabic-Indic digits fold to ASCII so `[٢]` resolves to source 2. */
function toAsciiDigits(value: string): string {
  return value.replace(/[٠-٩]/g, (char) =>
    String(char.charCodeAt(0) - 0x0660),
  );
}

export function CitationMarkers({
  text,
  citations,
  onCitationClick,
}: CitationMarkersProps): JSX.Element {
  const { t } = useTranslation();
  const known = new Set(citations.map((citation) => citation.index));

  const parts: (string | { index: number })[] = [];
  let cursor = 0;

  for (const match of text.matchAll(MARKER)) {
    const start = match.index ?? 0;
    if (start > cursor) parts.push(text.slice(cursor, start));

    const number = Number.parseInt(toAsciiDigits(match[1] ?? ""), 10);
    if (Number.isFinite(number) && known.has(number)) {
      // The brackets the model wrote are dropped: the chip is the marker now, and `[1]` inside a
      // chip reads as two markers. The Arabic-Indic form disappears with them, which is the point.
      parts.push({ index: number });
    } else {
      // Unknown marker: plain text, not a button that goes nowhere.
      parts.push(match[0]);
    }
    cursor = start + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));

  return (
    <>
      {parts.map((part, position) =>
        typeof part === "string" ? (
          // eslint-disable-next-line react/no-array-index-key -- text runs have no stable id
          <span key={`text-${position}`}>{part}</span>
        ) : (
          <CitationChip
            key={`marker-${position}-${part.index}`}
            index={part.index}
            onClick={() => onCitationClick(part.index)}
            label={t("citations.openSource", { index: part.index })}
          />
        ),
      )}
    </>
  );
}
